import { Injectable, signal, WritableSignal } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { JsonReply, NextLocator, SelectOption, SelectOptionGroup } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, map, Observable, of, Subject, tap } from 'rxjs';

import { ServicesService } from '../services/services.service';
import { ServiceTaskView } from '../services/services.types';
import {
    CancelTaskListBody,
    CancelTaskListReply,
    CheckDataQualityTaskRequest,
    ChangeServiceUserAssignmentTrainedBody,
    CompanyDataUser,
    DeleteTaskListReply,
    DeleteTaskListRequest,
    DownloadSearchTaskBody,
    DownloadTaskModelBody,
    FilterAddress,
    FlushBeautifyTask,
    FlushMultiTaskEditBody,
    FlushMultiTaskEditReply,
    FlushServiceUserAssignmentBody,
    FlushServiceUserAssignmentReply,
    FlushTaskBody,
    FlushTaskGoalBody,
    FlushTaskGoalReply,
    FlushTaskReply,
    Pagination,
    PlanningAreaAsOption,
    RemoveTaskBody,
    RemoveTaskReply,
    ResetTaskListBody,
    ResetTaskListReply,
    RetrieveMultiTaskEditBody,
    RetrieveMultiTaskEditReply,
    RetrieveNextTaskCustomGoalAutoAssignmentReply,
    RetrieveNextTaskCustomGoalAutoAssignmentRequest,
    RetrieveTaskAssignmentsBody,
    RetrieveTaskAssignmentsReply,
    RetrieveTaskByIdBody,
    RetrieveTaskByIdReply,
    RetrieveTaskGoalReply,
    RetrieveTaskSearchOptionsBody,
    RetrieveTaskSearchOptionsReply,
    RetrieveUserCalendarBody,
    RetrieveUserCalendarReply,
    RetrieveZombieCalendarBody,
    RetrieveZombieCalendarReply,
    SearchTaskBody,
    SearchTaskReply,
    SendMessageBody,
    SendTaskListBody,
    SendTaskListReply,
    StartEndBody, TaskSearch,
    TaskSearchOptionsFilter,
    TaskStatusEnum,
    UploadTaskExcelBody,
    UploadTaskModelResponse,
    UserCalendar,
    ZombieNumberCompany,
    ExecuteTaskCustomActionRequest,
    FlushTaskMultipleUsersBody,
    FlushTaskMultipleUsersReply,
    SearchUserTask
} from './tasks.types';
import { WebSocketMessage } from '../../../core/websocket/websocket.types';
import { DTypeService } from '../../../core/dtype/dtype.service';
import { HttpService } from '../../../core/http/http.service';
import { LocalizePipe } from '../../../core/pipes/localize.pipe';
import { UserService } from '../../../core/user/user.service';
import { StorageKeysEnum } from '../../../core/enums/storage.keys.enum';
import { NextMethodsEnum } from '../../../core/enums/next-methods.enum';

@Injectable({ providedIn: 'root' })
export class TasksService {

    public readonly chunkSize = 100;
    public readonly userChunkSize = 25;
    public count: number;

    private _taskView: WritableSignal<ServiceTaskView> = signal(ServiceTaskView.TASKLIST);
    readonly taskView = this._taskView.asReadonly();

    private _tasks: BehaviorSubject<TaskSearch[]> = new BehaviorSubject([]);
    private _task: BehaviorSubject<TaskSearch> = new BehaviorSubject(undefined);
    private _taskById: BehaviorSubject<RetrieveTaskByIdReply> = new BehaviorSubject(undefined);
    private _viewTasks: BehaviorSubject<TaskSearch[]> = new BehaviorSubject([]);
    private _tasksByUser: BehaviorSubject<SearchUserTask[]> = new BehaviorSubject([]);
    private _viewTasksByUser: BehaviorSubject<SearchUserTask[]> = new BehaviorSubject([]);
    private _pagination: BehaviorSubject<Pagination> = new BehaviorSubject(undefined);
    private _paginationByUser: BehaviorSubject<Pagination> = new BehaviorSubject(undefined);
    private _taskTypes: BehaviorSubject<SelectOptionGroup[]> = new BehaviorSubject(undefined);
    private _taskTypesForPlanning: BehaviorSubject<SelectOptionGroup[]> = new BehaviorSubject(undefined);
    private _planningAreas: BehaviorSubject<PlanningAreaAsOption[]> = new BehaviorSubject(undefined);
    private _planningArea: BehaviorSubject<PlanningAreaAsOption[]> = new BehaviorSubject(undefined);
    private _address: BehaviorSubject<FilterAddress> = new BehaviorSubject(undefined);
    private _dateFrom: BehaviorSubject<Date> = new BehaviorSubject(undefined);
    private _dateTo: BehaviorSubject<Date> = new BehaviorSubject(undefined);
    private _schedule: unknown;
    private _statusOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject(undefined);
    private _bodyWithFilters: BehaviorSubject<SearchTaskBody> = new BehaviorSubject(undefined);
    private statusOptionsWithoutObserver;

    private _reasonOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject(undefined);
    private _availableOptions: BehaviorSubject<TaskSearchOptionsFilter[]> = new BehaviorSubject(undefined);

    private _editButton: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _openEditSidebar: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _openBeautifySidebar: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _taskCopied: BehaviorSubject<string> = new BehaviorSubject(undefined);
    private _alreadyAlertedCopy: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private availableUsers: BehaviorSubject<CompanyDataUser[]> = new BehaviorSubject(undefined);
    private availableZombies: BehaviorSubject<ZombieNumberCompany[]> = new BehaviorSubject(undefined);
    private loading: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private _webSocketMessage: Subject<WebSocketMessage> = new Subject();


    /**
     * Constructor
     */
    constructor(
        private dtypeService: DTypeService,
        private httpService: HttpService,
        private localize: LocalizePipe,
        private serviceServices: ServicesService,
        private translocoService: TranslocoService,
        private usersService: UserService,
    ) {
        const paginationSize = Number(localStorage.getItem(StorageKeysEnum.taskPaginationSize));
        if (paginationSize && !Number.isNaN(paginationSize)) {
            this._pagination.next({
                length: 0,
                size: paginationSize,
                page: 0,
                lastPage: 0,
                startIndex: 0,
                endIndex: 0
            });
        }
        const paginationByUserSize = Number(localStorage.getItem(StorageKeysEnum.taskByUserPaginationSize));
        if (paginationByUserSize && !Number.isNaN(paginationByUserSize)) {
            this._paginationByUser.next({
                length: 0,
                size: paginationByUserSize,
                page: 0,
                lastPage: 0,
                startIndex: 0,
                endIndex: 0
            });
        }
    }

    get tasks$(): Observable<TaskSearch[]> {
        return this._tasks.asObservable();
    }

    /**
     * Getter for task
     */
    get task$(): Observable<TaskSearch> {
        return this._task.asObservable();
    }

    get taskById$(): Observable<RetrieveTaskByIdReply> {
        return this._taskById.asObservable();
    }

    get viewTasks$(): Observable<any[]> {
        return this._viewTasks.asObservable();
    }

    get tasksByUser$(): Observable<SearchUserTask[]> {
        return this._tasksByUser.asObservable();
    }

    get viewTasksByUser$(): Observable<SearchUserTask[]> {
        return this._viewTasksByUser.asObservable();
    }

    get pagination$(): Observable<any> {
        return this._pagination.asObservable();
    }

    get paginationByUser$(): Observable<any> {
        return this._paginationByUser.asObservable();
    }

    get taskTypes$(): Observable<SelectOptionGroup[]> {
        return this._taskTypes.asObservable();
    }

    get taskTypesForPlanning$(): Observable<SelectOptionGroup[]> {
        return this._taskTypesForPlanning.asObservable();
    }

    get planningAreas$(): Observable<PlanningAreaAsOption[]> {
        return this._planningAreas.asObservable();
    }

    get planningArea$(): Observable<PlanningAreaAsOption[]> {
        return this._planningArea.asObservable();
    }

    get address$(): Observable<FilterAddress> {
        return this._address.asObservable();
    }

    get dateFrom$(): Observable<Date> {
        return this._dateFrom.asObservable();
    }

    get dateTo$(): Observable<Date> {
        return this._dateTo.asObservable();
    }

    /**
     * Getter for status option list
     */
    get statusOptions$(): Observable<SelectOption[]> {
        return this._statusOptions.asObservable();
    }

    get reasonOptions$(): Observable<SelectOption[]> {
        return this._reasonOptions.asObservable();
    }

    get availableOptions$(): Observable<TaskSearchOptionsFilter[]> {
        return this._availableOptions.asObservable();
    }

    /**
     * Getter for body with filters
     */
    get bodyWithFilters$(): Observable<SearchTaskBody> {
        return this._bodyWithFilters.asObservable();
    }

    get editButton$(): Observable<boolean> {
        return this._editButton.asObservable();
    }

    get openEditSidebar$(): Observable<boolean> {
        return this._openEditSidebar.asObservable();
    }

    get openBeautifySidebar$(): Observable<boolean> {
        return this._openBeautifySidebar.asObservable();
    }

    get taskCopied$(): Observable<string> {
        return this._taskCopied.asObservable();
    }

    get alreadyAlertedCopy$(): Observable<boolean> {
        return this._alreadyAlertedCopy.asObservable();
    }

    get availableUsers$(): Observable<CompanyDataUser[]> {
        return this.availableUsers.asObservable();
    }

    get availableZombies$(): Observable<ZombieNumberCompany[]> {
        return this.availableZombies.asObservable();
    }

    get loading$(): Observable<boolean> {
        return this.loading.asObservable();
    }

    get webSocketMessage$(): Observable<WebSocketMessage> {
        return this._webSocketMessage.asObservable();
    }

    setTaskView(taskView: ServiceTaskView) {
        this._taskView.set(taskView);
    }

    getTask(taskId: string): Observable<TaskSearch> {
        let task = this._tasks.getValue()?.find(t => t.task?.taskId === taskId);
        if (!task) {
            for (const userTask of this._tasksByUser.getValue()) {
                task = userTask.taskList.find(t => t.task.taskId === taskId);
                if (task) {
                    break;
                }
            }
        }
        if ((this._task?.getValue()?.task?.taskId !== task?.task?.taskId) && (this._task?.getValue()?.oldTaskId !== task?.task?.taskId)) {
            this._task.next(task);
        }
        return of(task);
    }

    setNewTask(task: TaskSearch) {
        this._task.next(task);
    }

    setTaskById(task: RetrieveTaskByIdReply) {
        const fromTime = task?.row?.schedule?.timeFrom ?? null;
        const toTime = task?.row?.schedule?.timeTo ?? null;
        const fromTime2 = task?.row?.schedule?.timeFrom2 ?? null;
        const toTime2 = task?.row?.schedule?.timeTo2 ?? null;
        if (fromTime) {
            task.task.scheduled_from_time = fromTime;
        }
        if (toTime) {
            task.task.scheduled_to_time = toTime;
        }
        if (fromTime2) {
            task.task.scheduled_from_time2 = fromTime2;
        }
        if (toTime2) {
            task.task.scheduled_to_time2 = toTime2;
        }
        this._taskById.next(task);
    }

    setTasks(tasks: TaskSearch[]) {
        this._tasks.next(tasks);
    }

    setTasksByUser(tasks: SearchUserTask[]) {
        this._tasksByUser.next(tasks);
    }

    getViewTasks(body: SearchTaskBody, refresh: boolean = false, page: number = 0, size?: number): Observable<TaskSearch[]> {
        size = size ? size : this._pagination.getValue()?.size ?? 100;
        return this.searchTasks(body, page, size, refresh).pipe(
            map((tasks) => {
                // Clone thetasks to prevent accidental updates
                // let taskList = cloneDeep(this.sortTasks(tasks, field, dir));
                let taskList = cloneDeep(tasks);
                const tasksLength = this.count ?? tasks.length;

                // Paginate - Start

                // Calculate pagination details
                const begin = page * size;
                const end = Math.min((size * (page + 1)), tasksLength);
                const lastPage = Math.max(Math.ceil(tasksLength / size), 1);

                // Prepare the pagination object
                let pagination: Partial<Pagination>;

                // If the requested page number is bigger than
                // the last possible page number, return null for
                // tasks but also send the last possible page so
                // the app can navigate to there
                if (page > lastPage) {
                    pagination = {
                        lastPage
                    };
                } else {
                    taskList = taskList.slice(begin, end);

                    // Prepare the pagination mock-api
                    pagination = {
                        length: tasksLength,
                        size,
                        page,
                        lastPage: lastPage,
                        startIndex: begin,
                        endIndex: end - 1
                    };
                }

                localStorage.setItem(StorageKeysEnum.taskPaginationSize, size.toString(10));
                this._viewTasks.next(taskList);
                this._pagination.next(pagination as Pagination);
                return taskList;
            })
        );
    }

    getViewUsers(body: SearchTaskBody, refresh: boolean = false, page: number = 0, size?: number): Observable<any[]> {
        size = size ? size : this._paginationByUser.getValue()?.size ?? 25;
        return this.searchUsers(body, page, size, refresh).pipe(
            map((tasks) => {
                // Clone the tasks to prevent accidental updates
                // let taskList = cloneDeep(this.sortTasks(tasks, field, dir));
                let taskList = cloneDeep(tasks);
                const tasksLength = this.count ?? tasks.length;

                // Paginate - Start

                // Calculate pagination details
                const begin = page * size;
                const end = Math.min((size * (page + 1)), tasksLength);
                const lastPage = Math.max(Math.ceil(tasksLength / size), 1);

                // Prepare the pagination object
                let pagination: Partial<Pagination>;

                // If the requested page number is bigger than
                // the last possible page number, return null for
                // tasks but also send the last possible page so
                // the app can navigate to there
                if (page > lastPage) {
                    pagination = {
                        lastPage
                    };
                } else {
                    taskList = taskList.slice(begin, end);

                    // Prepare the pagination mock-api
                    pagination = {
                        length: tasksLength,
                        size,
                        page,
                        lastPage: lastPage,
                        startIndex: begin,
                        endIndex: end - 1
                    };
                }

                localStorage.setItem(StorageKeysEnum.taskByUserPaginationSize, size.toString(10));
                this._viewTasksByUser.next(taskList);
                this._paginationByUser.next(pagination as Pagination);
                return tasks;
            })
        );
    }

    updateTask(body: FlushTaskBody): Observable<FlushTaskReply> {
        return this.flushTask(body);
    }

    beautifyTask(body: FlushBeautifyTask): Observable<FlushTaskReply> {
        return this.httpService.post<FlushTaskReply>(NextMethodsEnum.flushBeautifyTask, body);
    }

    flushTaskMultipleUsers(body: FlushTaskMultipleUsersBody) {
        return this.httpService.post<FlushTaskMultipleUsersReply>(NextMethodsEnum.flushTaskMultipleUsers, body);
    }

    getTaskAssignments(serviceId: string, planningArea: string, taskDate: string) {
        return this.retrieveTaskAssignments({ serviceId: serviceId, planningAreaId: planningArea, taskDate }).pipe(
            map(reply => reply[planningArea])
        );
    }

    getAllAreasTaskAssignments(serviceId: string, taskDate: string) {
        return this.retrieveTaskAssignments({ serviceId, taskDate });
    }

    getUserCalendar(request: RetrieveUserCalendarBody) {
        return this.httpService.post<RetrieveUserCalendarReply>(NextMethodsEnum.retrieveUserCalendar, request);
    }

    getZombieCalendar(serviceId: string, planningArea: string, date: string, zombieNumber: number): Observable<UserCalendar[]> {
        return this.retrieveZombieCalendar({ serviceId: serviceId, planningArea: planningArea, date: date, zombieNumber: zombieNumber }).pipe(
            map(reply => reply.calendar)
        );
    }

    setEditButton(editButton: boolean) {
        this._editButton.next(editButton);
    }

    setAlreadyAlertedCopy(alreadyAlertedCopy: boolean) {
        this._alreadyAlertedCopy.next(alreadyAlertedCopy);
    }

    setOpenEditSidebar(openEditSidebar: boolean) {
        this._openEditSidebar.next(openEditSidebar);
    }

    setOpenBeautifySidebar(openBeautifySidebar: boolean) {
        this._openBeautifySidebar.next(openBeautifySidebar);
    }

    setPlanningArea(planningArea: PlanningAreaAsOption[]) {
        this._planningArea.next(planningArea);
    }

    setDateFrom(date: Date) {
        this._dateFrom.next(date);
    }

    setDateTo(date: Date) {
        this._dateTo.next(date);
    }

    setBodyWithFilters(body: SearchTaskBody) {
        this._bodyWithFilters.next(body);
    }

    setTaskCopied(task: string) {
        this._taskCopied.next(task);
    }

    setAvailableUsers(users: CompanyDataUser[]) {
        this.availableUsers.next(users);
    }

    setAvailableZombies(zombies: ZombieNumberCompany[]) {
        this.availableZombies.next(zombies);
    }

    setViewTasks(taskList: TaskSearch[]) {
        this._viewTasks.next(taskList);
    }

    setLoading(loading: boolean) {
        this.loading.next(loading);
    }

    setWebSocketMessage(message: WebSocketMessage) {
        this._webSocketMessage.next(message);
    }

    searchAddress(expr: string, uri: string) {
        return this.dtypeService.searchDTypeDataCollection({
            expr,
            uri
        });
    }

    searchName(serviceId: string, expr: string, planningArea: string | string[]) {
        const locator: NextLocator = {
            serviceId
        };
        return this.usersService.searchCompanyUserDN(
            locator,
            expr,
            planningArea
        );
    }

    getTaskSearchOptions(body: RetrieveTaskSearchOptionsBody) {
        return this.httpService.post<RetrieveTaskSearchOptionsReply>(NextMethodsEnum.retrieveTaskSearchOptions, body).pipe(
            tap((reply) => {
                this._availableOptions.next(reply.availableSearchOptions);
                this._planningAreas.next(reply.planningArea);
                this._taskTypes.next(reply.taskType);
                this._taskTypesForPlanning.next(reply.taskTypeForPlanning);
                this._schedule = reply.schedule;

                reply.statuses.splice(reply.statuses.findIndex(s => s.code === 'REJECTED'),1);
                this._statusOptions.next(reply.statuses);

                this.statusOptionsWithoutObserver = reply.statuses;
                this._reasonOptions.next(reply.reason);
                this._address.next(reply.address);
                return reply;
            })
        );
    }

    getSavedAddressAndEventSummary(body: RetrieveTaskByIdBody): Observable<RetrieveTaskByIdReply> {
        return this.retrieveTaskById(body);
    }

    sendTaskList(body: SendTaskListBody) {
        return this.httpService.post<SendTaskListReply>(NextMethodsEnum.sendTaskList, body);
    }

    cancelTaskList(body: CancelTaskListBody) {
        return this.httpService.post<CancelTaskListReply>(NextMethodsEnum.cancelTaskList, body);
    }
    resetTaskList(body: ResetTaskListBody) {
        return this.httpService.post<ResetTaskListReply>(NextMethodsEnum.resetTaskList, body);
    }

    removeTask(body: RemoveTaskBody) {
        return this.httpService.post<RemoveTaskReply>(NextMethodsEnum.deleteTask, body);
    }

    deleteTaskList(body: DeleteTaskListRequest) {
        return this.httpService.post<DeleteTaskListReply>(NextMethodsEnum.deleteTaskList, body);
    }

    flushServiceUserAssignment(body: FlushServiceUserAssignmentBody) {
        return this.httpService.post<FlushServiceUserAssignmentReply>(NextMethodsEnum.flushServiceUserAssignment, body);
    }

    changeServiceUserAssignmentTrained(body: ChangeServiceUserAssignmentTrainedBody) {
        return this.httpService.post<FlushServiceUserAssignmentReply>(NextMethodsEnum.changeServiceUserAssignmentTrained, body);
    }

    getMultiTaskEdit(serviceId: string, tasks: string[]) {
        return this.retrieveMultiTaskEdit({ serviceId: serviceId, taskId: tasks });
    }

    flushMultiTaskEdit(body: FlushMultiTaskEditBody) {
        return this.httpService.post<FlushMultiTaskEditReply>(NextMethodsEnum.flushMultiTaskEdit, body);
    }

    downloadSearchTask(body: DownloadSearchTaskBody): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadSearchTaskXSSF, body, 'blob');
    }

    downloadTaskModel(body: DownloadTaskModelBody): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadServiceTasksModelXSSF, body, 'blob');
    }

    uploadExcel(body: UploadTaskExcelBody): Observable<UploadTaskModelResponse> {
        return this.httpService.post<UploadTaskModelResponse>(NextMethodsEnum.flushDTypeExcelUpload, body);
    }

    sendMessage(body: SendMessageBody) {
        return this.httpService.post<any>(NextMethodsEnum.sendNotificationForTask, body);
    }

    startEndTask(body: StartEndBody) {
        return this.httpService.post<any>(NextMethodsEnum.forceTaskStartEnd, body);
    }

    retrieveTaskById(body: RetrieveTaskByIdBody) {
        return this.httpService.post<RetrieveTaskByIdReply>(NextMethodsEnum.retrieveTaskById, body);
    }

    retrieveTaskGoal(taskId: string, serviceId: string) {
        return this.httpService.post<RetrieveTaskGoalReply>(NextMethodsEnum.retrieveNextTaskGoal, { taskId, serviceId })
            .pipe(map(reply => reply?.goals ?? []));
    }

    flushTaskGoal(body: FlushTaskGoalBody) {
        return this.httpService.post<FlushTaskGoalReply>(NextMethodsEnum.flushNextTaskGoal, body)
            .pipe(map(reply => reply?.goalStatus));
    }

    retrieveNextTaskCustomGoalAutoAssignment(body: RetrieveNextTaskCustomGoalAutoAssignmentRequest) {
        return this.httpService.post<RetrieveNextTaskCustomGoalAutoAssignmentReply>(NextMethodsEnum.retrieveNextTaskCustomGoalAutoAssignment, body)
            .pipe(map(reply => reply?.goal ?? {}));
    }

    checkDataQualityTaskList(body: CheckDataQualityTaskRequest) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.checkDataQualityTaskList, body);
    }

    executeTaskCustomAction(request: ExecuteTaskCustomActionRequest) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.executeTaskCustomAction, request);
    }

    private searchTaskList(body: SearchTaskBody) {
        return this.httpService.post<SearchTaskReply>(NextMethodsEnum.searchTask, body);
    }

    private flushTask(body: FlushTaskBody) {
        return this.httpService.post<FlushTaskReply>(NextMethodsEnum.flushTask, body);
    }
    
    private searchTasks(body: SearchTaskBody, page: number, size: number, refresh: boolean = false): Observable<TaskSearch[]> {
        const tasks = this._tasks.getValue();
        body.isGroupedByUser = false;
        body.lastConsumedUserId = undefined;
        this._tasksByUser.next([]);
        this._viewTasksByUser.next([]);
        if (refresh || !tasks || (tasks.length > 0 && body.locator.serviceId !== tasks[0].task?.serviceId)) {
            body.lastConsumedTaskID = undefined;
            this.setBodyWithFilters(body);
            return this.searchTaskList(body).pipe(
                map(reply => this.processPartaker(reply)),
                tap(newTasks => this._tasks.next(newTasks))
            );
        } else if (page * size >= tasks.length) {
            body.lastConsumedTaskID = tasks[tasks.length - 1].task.taskId;
            this.setBodyWithFilters(body);
            return this.searchTaskList(body).pipe(
                map((reply) => {
                    const newTasks = this.processPartaker(reply);
                    tasks.push(...newTasks);
                    this._tasks.next(tasks);
                    return tasks;
                }),
            );
        } else {
            return this.tasks$;
        }
    }

    private searchUsers(body: SearchTaskBody, page: number, size: number, refresh: boolean = false): Observable<SearchUserTask[]> {
        const tasks = this._tasksByUser.getValue();
        body.isGroupedByUser = true;
        body.lastConsumedTaskID = undefined;
        this._tasks.next([]);
        this._viewTasks.next([]);
        if (refresh || !tasks || (tasks.length > 0 && body.locator.serviceId !== tasks[0].taskList[0]?.task?.serviceId)) {
            body.lastConsumedUserId = undefined;
            this.setBodyWithFilters(body);
            return this.searchTaskList(body).pipe(
                map(reply => this.processUserTasksPartaker(reply)),
                tap(newTasks => this._tasksByUser.next(newTasks))
            );
        } else if (page * size >= tasks.length) {
            body.lastConsumedUserId = tasks[tasks.length - 1].userId;
            this.setBodyWithFilters(body);
            return this.searchTaskList(body).pipe(
                map((reply) => {
                    const newTasks = this.processUserTasksPartaker(reply);
                    tasks.push(...newTasks);
                    this._tasksByUser.next(tasks);
                    return tasks;
                }),
            );
        } else {
            return this.tasksByUser$;
        }
    }

    private processPartaker(reply: SearchTaskReply) {
        const userUri = this.serviceServices.userUri;
        const marcaUri = this.serviceServices.DTYPE_BRAND_URI;
        const clienteUri = this.serviceServices.DTYPE_CUSTOMER_URI;
        const projectUri = this.serviceServices.DTYPE_PROJECT_URI;
        const serviceUri = this.serviceServices.DTYPE_SERVICE_URI;
        const usersPartaker = reply.partaker[userUri];
        this.count = reply.count;
        const newTasks = reply.task ?? [];
        for (const t of newTasks) {
            if (usersPartaker[t.who.userId]) {
                t.who.userName = usersPartaker[t.who.userId].displayName;
            }
            if (reply.partaker[marcaUri][t.task?.marca_code]) {
                t.task.marca_name = reply.partaker[marcaUri][t.task?.marca_code].marca_name;
            }
            if (reply.partaker[clienteUri][t.task?.marca_code + '+' + t.task?.cliente_code]) {
                t.task.cliente_name = reply.partaker[clienteUri][t.task?.marca_code + '+' + t.task?.cliente_code].name;
            }
            if (reply.partaker[projectUri][t.task?.projectId]) {
                t.task.project_name = reply.partaker[projectUri][t.task?.projectId].name;
            }
            if (reply.partaker[serviceUri][t.task?.projectId]) {
                t.task.service_name = reply.partaker[serviceUri][t.task?.serviceId].name;
            }
        }
        return newTasks;
    }

    private processUserTasksPartaker(reply: SearchTaskReply) {
        const userUri = this.serviceServices.userUri;
        const marcaUri = this.serviceServices.DTYPE_BRAND_URI;
        const clienteUri = this.serviceServices.DTYPE_CUSTOMER_URI;
        const projectUri = this.serviceServices.DTYPE_PROJECT_URI;
        const serviceUri = this.serviceServices.DTYPE_SERVICE_URI;
        const usersPartaker = reply.partaker[userUri];
        this.count = reply.count;
        const userTasks: SearchUserTask[] = [];
        for (const userTask of reply.taskByUser ?? []) {
            userTask.displayName = usersPartaker[userTask.userId]?.displayName;
            userTask.sAMAccountName = usersPartaker[userTask.userId]?.sAMAccountName;
            for (const t of userTask.taskList) {
                if (usersPartaker[t.who.userId]) {
                    t.who.userName = usersPartaker[t.who.userId].displayName;
                }
                if (reply.partaker[marcaUri][t.task?.marca_code]) {
                    t.task.marca_name = reply.partaker[marcaUri][t.task?.marca_code].marca_name;
                }
                if (reply.partaker[clienteUri][t.task?.marca_code + '+' + t.task?.cliente_code]) {
                    t.task.cliente_name = reply.partaker[clienteUri][t.task?.marca_code + '+' + t.task?.cliente_code].name;
                }
                if (reply.partaker[projectUri][t.task?.projectId]) {
                    t.task.project_name = reply.partaker[projectUri][t.task?.projectId].name;
                }
                if (reply.partaker[serviceUri][t.task?.projectId]) {
                    t.task.service_name = reply.partaker[serviceUri][t.task?.serviceId].name;
                }
            }
            userTasks.push(userTask);
        }
        return userTasks;
    }

    private compareStrings(string1: string, string2: string, dir: SortDirection) {
        if (string1 === '') {
            if (string2 === '') {
                return 0;
            } else {
                return dir === 'asc' ? 0 : -1;
            }
        } else if (string2 === '') {
            if (string1 === '') {
                return 0;
            } else {
                return dir === 'asc' ? -1 : 0;
            }
        } else {
            return dir === 'asc' ? string1.localeCompare(string2) : string2.localeCompare(string1);
        }
    }

    private getStatusLabel(statusCode: TaskStatusEnum) {
        const option = this.statusOptionsWithoutObserver.find(so => so.code === statusCode);
        return option?.value;
    }

    private sortTasks(tasks: TaskSearch[], field: string, dir: SortDirection) {
        return tasks.sort((a, b) => {
            switch (field) {
                case 'info':
                    if (!a.alert) {
                        if (!b.alert) {
                            return 0;
                        } else {
                            return dir === 'asc' ? 0 : -1;
                        }
                    } else if (!b.alert) {
                        if (!a.alert) {
                            return 0;
                        } else {
                            return dir === 'asc' ? -1 : 0;
                        }
                    } else {
                        return this.compareStrings(a.alert[0].type ?? '', b.alert[0].type ?? '', dir);
                    }
                case 'taskId':
                    return this.compareStrings(a.task.taskId ?? '', b.task.taskId ?? '', dir);
                case 'zombieName':
                    return this.compareStrings(a.who.zombieName ?? '', b.who.zombieName ?? '', dir);
                case 'userName':
                    return this.compareStrings(a.who.userName ?? '', b.who.userName ?? '', dir);
                case 'planningAreaName':
                    return this.compareStrings(a.group.planningAreaName ?? '', b.group.planningAreaName ?? '', dir);
                case 'taskTypeName':
                    return this.compareStrings(a.task.taskTypeName ?? '', b.task.taskTypeName ?? '', dir);
                case 'scheduled_from_date':
                    return this.compareStrings(a.schedule.timeFrom ?? '', b.schedule.timeFrom ?? '', dir);
                case 'addressName':
                    return this.compareStrings(a.where.addressName ?? '', b.where.addressName ?? '', dir);
                case 'fullAddress':
                    return this.compareStrings(a.where.addressVicinity ?? '', b.where.addressVicinity ?? '', dir);
                case 'timestamp':
                    return this.compareStrings(a.status.timestamp ?? '', b.status.timestamp ?? '', dir);
                case 'status':
                    const stsA = this.getStatusLabel(a.status.status);
                    const stsB = this.getStatusLabel(b.status.status);
                    return (this.compareStrings((this.localize.transform(stsA)) ?? '', (this.localize.transform(stsB)) ?? '', dir) === 0 ?
                        this.compareStrings((a.status.timestamp) ?? '', b.status.timestamp ?? '', dir) : this.compareStrings(this.translocoService.translate(a.status.status) ?? '',
                            this.translocoService.translate(a.status.status) ?? '', dir));
            }
            return 0;
        });
    }

    private retrieveTaskAssignments(body: RetrieveTaskAssignmentsBody) {
        return this.httpService.post<RetrieveTaskAssignmentsReply>(NextMethodsEnum.retrieveTaskAssignments, body);
    }

    private retrieveZombieCalendar(body: RetrieveZombieCalendarBody) {
        return this.httpService.post<RetrieveZombieCalendarReply>(NextMethodsEnum.retrieveZombieCalendar, body);
    }

    private retrieveMultiTaskEdit(body: RetrieveMultiTaskEditBody) {
        return this.httpService.post<RetrieveMultiTaskEditReply>(NextMethodsEnum.retrieveMultiTaskEdit, body);
    }

}
